import { useState } from 'react';
import { Link } from 'react-router-dom';

const FORM_URL = 'https://readdy.ai/api/form/d9p0rc8senbniolesnl0';

export default function About() {
  const [formUrl, setFormUrl] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!FORM_URL) return;

    setFormLoading(true);
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('phone_alt')?.toString().trim();
    if (honeypot) {
      setFormStatus('success');
      setFormLoading(false);
      return;
    }
    formData.delete('phone_alt');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, value.toString());
      });

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        if (serverMsg && typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试。');
    } finally {
      setFormLoading(false);
    }
  };

  const milestones = [
    { year: '2018', title: '平台创立', desc: '免费漫画网正式上线，专注提供正版韩漫阅读服务。' },
    { year: '2019', title: '版权合作', desc: '与韩国5大漫画平台签订合作协议，引进超过3000部正版韩漫。' },
    { year: '2020', title: '用户突破', desc: '注册用户突破100万，月活跃用户超过50万。' },
    { year: '2021', title: '技术升级', desc: '推出下拉式阅读模式，大幅提升移动端阅读体验。' },
    { year: '2022', title: '内容扩充', desc: '漫画库扩展至8000+部，涵盖恋爱、奇幻、动作、悬疑等全品类。' },
    { year: '2023', title: '行业认可', desc: '获得"最佳正版漫画平台"行业奖项，成为国内韩漫领域的标杆平台。' },
    { year: '2024', title: '千万用户', desc: '累计注册用户突破1000万，月活用户超500万。' },
  ];

  return (
    <main className="pt-24 pb-16 bg-background-50 min-h-screen">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
          <span>/</span>
          <span className="text-foreground-800 font-medium">关于我们</span>
        </nav>

        {/* Page Title */}
        <div className="text-center mb-12">
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">关于免费漫画网</h1>
          <p className="text-sm text-foreground-500 max-w-2xl mx-auto">
            正版韩漫大全，免费韩漫下拉式阅读平台。致力于为中文读者提供最优质、最合法的在线漫画阅读体验。
          </p>
        </div>

        {/* Brand Introduction */}
        <section className="mb-16">
          <h2 className="text-xl font-semibold font-heading text-foreground-800 mb-6 text-center">品牌介绍</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                免费漫画网成立于2018年，是国内领先的<strong>正版韩漫在线阅读平台</strong>。我们与韩国多家顶级漫画出版社和平台建立了长期战略合作关系，为中国读者提供内容合法、画质优越的韩漫阅读服务。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                平台采用先进的<strong>下拉式阅读技术</strong>，专为移动端优化的纵向滚动阅读体验，让读者无需翻页即可流畅阅读。无论是在通勤路上、休息时间还是睡前放松，都能享受极致的漫画阅读体验。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                我们坚信<strong>正版内容</strong>的价值，每一部作品都获得了合法授权。这不仅保护了创作者的权益，也为读者提供了更高质量的内容保障。
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden h-64 md:h-80 bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=A%20modern%20cozy%20reading%20space%20with%20a%20tablet%20displaying%20colorful%20Korean%20manhwa%20art%2C%20warm%20ambient%20lighting%2C%20bookshelf%20background%2C%20minimalist%20aesthetic%2C%20comfortable%20atmosphere%2C%20professional%20photography%20style&width=800&height=600&seq=about-brand&orientation=landscape"
                alt="免费漫画网品牌形象"
                title="免费漫画网 - 正版韩漫阅读平台"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="mb-16 bg-background-100 rounded-2xl p-8 md:p-10">
          <h2 className="text-xl font-semibold font-heading text-foreground-800 mb-8 text-center">我们的价值观</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-shield-check-line', title: '正版授权', desc: '所有内容均获得合法授权，保护创作者权益，为读者提供品质保障。' },
              { icon: 'ri-image-line', title: '高清画质', desc: '采用高分辨率图片传输技术，确保每一页漫画都清晰细腻。' },
              { icon: 'ri-smartphone-line', title: '极致体验', desc: '专为移动端优化的下拉式阅读，流畅丝滑，沉浸其中。' },
              { icon: 'ri-user-heart-line', title: '用户至上', desc: '以用户需求为核心，持续优化产品，提供最优质的阅读服务。' },
            ].map((value) => (
              <div key={value.title} className="text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100 text-primary-500">
                  <i className={`${value.icon} text-xl`}></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-2">{value.title}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Milestones */}
        <section className="mb-16">
          <h2 className="text-xl font-semibold font-heading text-foreground-800 mb-8 text-center">发展历程</h2>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
            <div className="space-y-8">
              {milestones.map((item, index) => (
                <div key={item.year} className={`relative flex items-start gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right md:pr-10' : 'md:text-left md:pl-10'} ${index % 2 !== 0 ? 'md:hidden' : ''}`}>
                    {/* desktop left side */}
                  </div>
                  <div className="absolute left-4 md:left-1/2 w-2 h-2 rounded-full bg-primary-500 ring-4 ring-background-50 -translate-x-1/2 mt-1.5 z-10"></div>
                  <div className={`flex-1 pl-10 md:pl-0 ${index % 2 === 0 ? 'md:pl-10 md:pr-0' : 'md:pr-10 md:pl-0'} ${index % 2 === 0 ? '' : 'md:text-right'}`}>
                    <span className="text-xs font-bold text-primary-500">{item.year}</span>
                    <h3 className="text-sm font-semibold text-foreground-800 mt-1">{item.title}</h3>
                    <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{item.desc}</p>
                  </div>
                  {index % 2 !== 0 && (
                    <div className="flex-1 hidden md:block"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="mb-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: '10,000+', label: '正版漫画', icon: 'ri-book-open-line' },
              { value: '1,000万+', label: '注册用户', icon: 'ri-user-line' },
              { value: '98%', label: '好评率', icon: 'ri-thumb-up-line' },
              { value: '8年', label: '运营经验', icon: 'ri-time-line' },
            ].map((stat) => (
              <div key={stat.label} className="text-center p-6 bg-background-100 rounded-xl border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 text-primary-500">
                  <i className={`${stat.icon} text-xl`}></i>
                </div>
                <div className="text-xl md:text-2xl font-bold text-foreground-900">{stat.value}</div>
                <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Form */}
        <section className="mb-16 bg-background-100 rounded-2xl p-8 md:p-10">
          <h2 className="text-xl font-semibold font-heading text-foreground-800 mb-2 text-center">联系我们</h2>
          <p className="text-sm text-foreground-500 text-center mb-8">
            有任何问题或建议？请填写以下表单，我们会在1-2个工作日内回复。
          </p>

          {formStatus === 'success' ? (
            <div className="text-center py-10">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-accent-100 text-accent-500">
                <i className="ri-check-line text-3xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-800 mb-2">提交成功！</h3>
              <p className="text-sm text-foreground-500">感谢您的留言，我们会尽快回复。</p>
              <button
                onClick={() => setFormStatus('idle')}
                className="mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer"
              >
                发送新消息
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="max-w-lg mx-auto"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入留言主题"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言（不超过500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
              </div>

              {/* Honeypot */}
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="contact-field-alt"
              />

              {formStatus === 'error' && formError && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                  {formError}
                </div>
              )}

              <button
                type="submit"
                disabled={formLoading}
                className="w-full py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {formLoading ? '提交中...' : '提交留言'}
              </button>
            </form>
          )}
        </section>

        {/* Contact Info */}
        <section className="text-center">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="p-6 bg-background-100 rounded-xl border border-background-200/70">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-accent-100 text-accent-500">
                <i className="ri-mail-line text-lg"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</h3>
              <p className="text-xs text-foreground-500">contact@freemanhua.com</p>
            </div>
            <div className="p-6 bg-background-100 rounded-xl border border-background-200/70">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-accent-100 text-accent-500">
                <i className="ri-map-pin-line text-lg"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-800 mb-1">公司地址</h3>
              <p className="text-xs text-foreground-500">中国北京市朝阳区</p>
            </div>
            <div className="p-6 bg-background-100 rounded-xl border border-background-200/70">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-accent-100 text-accent-500">
                <i className="ri-customer-service-line text-lg"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-800 mb-1">在线客服</h3>
              <p className="text-xs text-foreground-500">工作日 9:00 - 18:00</p>
            </div>
          </div>
        </section>
      </div>

      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": "关于免费漫画网",
        "description": "免费漫画网成立于2018年，是国内领先的正版韩漫在线阅读平台。",
        "url": "https://www.freemanhua.com/about",
        "isPartOf": {
          "@type": "WebSite",
          "name": "免费漫画网",
          "url": "https://www.freemanhua.com/"
        }
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.freemanhua.com/" },
          { "@type": "ListItem", "position": 2, "name": "关于我们", "item": "https://www.freemanhua.com/about" },
        ],
      }) }} />
    </main>
  );
}